import React from 'react';
import { Button, FormControlLabel, Grid, InputAdornment, InputLabel, MenuItem, Select, Switch, TextField } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import DateHelperInstance from '../../../services/DateHelper';
import IEventBaseDataProps from './IEventBaseDataProps';
import HelpTooltip from '../../common/HelpTooltip';

interface IGeneralData {
    name: string;
    description: string;
    eventDate: Date;
    raceTypeId: string;
    raceMode: boolean;
    racePrivate: boolean;
    allowRounds?: boolean;
    registrationUrl?: string;
    accessCode?: string;
}

export interface IGeneralDataErrors {
    name?: string;
    description?: string;
    eventDate?: string;
    raceTypeId?: string;
    registrationUrl?: string;
    accessCode?: string;
}

interface IEventGeneralDataProps extends IEventBaseDataProps<IGeneralData> {
    errors?: IGeneralDataErrors;
}

// interface IEventGeneralDataProps {
//     data: IGeneralData,
//     onDataChange?: (event: React.ChangeEvent<{}>, value: IGeneralData) => void;
// }

function EventGeneralData(props: IEventGeneralDataProps) {
    const { t } = useTranslation("common");
    const data = props.data;
    // const [name, setName] = useState<string>(props.name)
    // const [description, setDescription] = useState<string>(props.description);

    const triggerDataChange = (newData: IGeneralData) => {
        if (props.onDataChange) {
            props.onDataChange(newData);
        }
    }

    const generateAccessCode = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let code = '';
        for (let i = 0; i < 6; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        triggerDataChange({ ...data, accessCode: code });
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} sm={7} >
                <TextField id="eventName"
                    required
                    label={<>{t("eventsetup.name")}<HelpTooltip text={t("tooltips.eventName")} /></>}
                    value={data.name ?? ''}
                    fullWidth={true}
                    error={Boolean(props.errors?.name)}
                    helperText={props.errors?.name}
                    onChange={e => {
                        data.name = e.target.value;
                        triggerDataChange(data);
                    }} />
            </Grid>
            <Grid item xs={12} sm={5}>
                <TextField id="eventDate"
                    required
                    label={<>{t("eventsetup.eventDate")}<HelpTooltip text={t("tooltips.eventDate")} /></>}
                    value={DateHelperInstance.datePickString(data.eventDate)}
                    // value="2021-06-07"
                    type="date"
                    error={Boolean(props.errors?.eventDate)}
                    helperText={props.errors?.eventDate}
                    InputLabelProps={{ shrink: true }}
                    onChange={e => {
                        data.eventDate = new Date(e.target.value);
                        triggerDataChange(data);
                    }} />
            </Grid>
            
            <Grid item xs={12} sm={8} >
                <TextField id="eventDescription"
                    label={<>{t("eventsetup.description")}<HelpTooltip text={t("tooltips.eventDescription")} /></>}
                    value={data.description ?? ''}
                    fullWidth={true}
                    error={Boolean(props.errors?.description)}
                    helperText={props.errors?.description}
                    onChange={e => {
                        data.description = e.target.value;
                        triggerDataChange(data);
                    }} />
            </Grid>
            <Grid item xs={12} sm={4}>
                <InputLabel id="eventTypeLabel">{t("eventsetup.eventType")}<HelpTooltip text={t("tooltips.eventType")} /></InputLabel>
                <Select id="eventType"
                    labelId="eventTypeLabel"
                    value={data.raceTypeId ?? ''}
                    onChange={e => {
                        data.raceTypeId = e.target.value as string;
                        triggerDataChange(data);
                    }}>
                    <MenuItem value="1">{t("eventsetup.typebike")}</MenuItem>
                    <MenuItem value="2">{t("eventsetup.typecar")}</MenuItem>
                    <MenuItem value="3">{t("eventsetup.typekart")}</MenuItem>

                </Select>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                    control={<Switch
                        checked={data.raceMode}
                        onChange={e => {
                            data.raceMode = e.target.checked
                            triggerDataChange(data);

                        }} />}
                    label={<>{t("eventsetup.allParticipants")}<HelpTooltip text={t("tooltips.allParticipants")} /></>}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                    control={<Switch
                        checked={data.racePrivate}
                        onChange={e => {
                            data.racePrivate = e.target.checked
                            triggerDataChange(data);

                        }} />}
                    label={<>{t("eventsetup.privateEvent")}<HelpTooltip text={t("tooltips.privateEvent")} /></>}
                />

            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                    control={<Switch
                        checked={data.allowRounds ?? false}
                        onChange={e => {
                            data.allowRounds = e.target.checked;
                            triggerDataChange(data);
                        }} />}
                    label={<>{t("eventsetup.allowRounds")}<HelpTooltip text={t("tooltips.allowRounds")} /></>}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <TextField id="accessCode"
                    inputProps={{
                        readOnly: true
                    }}
                    label={<>{t("eventsetup.accessCode")}<HelpTooltip text={t("tooltips.accessCode")} /></>}
                    value={data.accessCode ?? ''}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Button
                                    size="small"
                                    onClick={generateAccessCode}
                                    title={t("eventsetup.generateAccessCode") as string}
                                    aria-label={t("eventsetup.generateAccessCode") as string}
                                >
                                    <RefreshIcon fontSize="small" />
                                </Button>
                            </InputAdornment>
                        )
                    }}
                />

            </Grid>



        </Grid>

    );
}

export default EventGeneralData;